import axios, { AxiosError, isAxiosError } from 'axios';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Empêche l'intercepteur de tenter un refresh + redirection sur un 401 (ex: vérification de session au montage, où un 401 est un résultat normal pour un visiteur anonyme). */
    skipAuthRefresh?: boolean;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

// Les tokens vivent dans des cookies httpOnly posés par l'API (voir apps/api/src/auth/auth.controller.ts) :
// withCredentials fait envoyer/recevoir ces cookies sur chaque requête, sans jamais les exposer au JS.
export const api = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

// NestJS renvoie { message, statusCode, error } ; message peut être une string ou un tableau
// (erreurs de validation class-validator groupées). On ne remonte que le premier message utile.
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!isAxiosError(error)) return fallback;
  const message = (error.response?.data as { message?: string | string[] } | undefined)?.message;
  if (Array.isArray(message)) return message[0] ?? fallback;
  return message ?? fallback;
}

let refreshPromise: Promise<void> | null = null;

async function refreshSession(): Promise<void> {
  await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (typeof error.config & { _retry?: boolean }) | undefined;

    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (originalRequest.url?.includes('/auth/')) {
      return Promise.reject(error);
    }

    // Les visiteurs anonymes (pages vitrine) déclenchent un GET /profile au montage de
    // AuthProvider : sans cookie, il échoue en 401 par design, pas la peine de tenter un
    // refresh (qui échouera aussi) ni de rediriger — ça ne concerne que la vérification de session.
    if (originalRequest.skipAuthRefresh) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      refreshPromise ??= refreshSession().finally(() => {
        refreshPromise = null;
      });
      await refreshPromise;
      return api(originalRequest);
    } catch (refreshError) {
      // Évite une boucle de rechargement si on est déjà sur /login (ou une page publique
      // sans session) : ne redirige que si on quitte réellement une zone protégée.
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    }
  },
);
